import React, { useEffect, useState } from 'react';
import { LaserEyesProvider as Provider, MAINNET } from '@omnisat/lasereyes';

// Definir a interface Window para incluir as propriedades das carteiras
declare global {
  interface Window {
    unisat?: any;
    xverse?: any;
    xverseWallet?: any;
    bitcoin?: {
      xverse?: any;
      [key: string]: any;
    };
    phantom?: any;
    okx?: any;
    wizz?: any;
    oyl?: any;
    [key: string]: any; // Para permitir propriedades dinâmicas
  }
}

interface LaserEyesProviderProps {
  children: React.ReactNode;
}

const LaserEyesProvider: React.FC<LaserEyesProviderProps> = ({ children }) => {
  const [isReady, setIsReady] = useState(false);

  // Função para verificar a disponibilidade das carteiras
  const checkWalletAvailability = () => {
    if (typeof window !== 'undefined') {
      // Verificação mais detalhada para Xverse
      // A Xverse pode estar disponível de várias formas diferentes
      const isXverseAvailable = () => {
        // Verificar se o objeto xverse existe diretamente
        if (typeof window.xverse !== 'undefined') return true;

        // Verificar se o objeto xverse está disponível através do bitcoin
        if (typeof window.bitcoin !== 'undefined' && typeof window.bitcoin.xverse !== 'undefined') return true;

        // Verificar se há algum método específico da Xverse disponível
        if (typeof window.xverseWallet !== 'undefined') return true;

        // Verificar se há algum método específico da Xverse no objeto window
        const hasXverseMethod = Object.keys(window).some(key =>
          key.toLowerCase().includes('xverse') ||
          (typeof window[key] === 'object' && window[key] !== null && Object.keys(window[key]).some(subKey => subKey.toLowerCase().includes('xverse')))
        );

        return hasXverseMethod;
      };

      const wallets = {
        unisat: typeof window.unisat !== 'undefined',
        xverse: isXverseAvailable(),
        oyl: typeof window.oyl !== 'undefined',
        phantom: typeof window.phantom !== 'undefined',
        okx: typeof window.okx !== 'undefined',
        wizz: typeof window.wizz !== 'undefined',
      };

      console.log('[LaserEyesProvider] Carteiras disponíveis:', wallets);

      // Verificar se pelo menos uma carteira está disponível
      const hasAnyWallet = Object.values(wallets).some(available => available);

      if (hasAnyWallet) {
        console.log('[LaserEyesProvider] Pelo menos uma carteira está disponível');
        setIsReady(true);
      } else {
        console.log('[LaserEyesProvider] Nenhuma carteira detectada ainda');
      }

      return hasAnyWallet;
    }
    return false;
  };

  // Efeito para verificar a disponibilidade das carteiras periodicamente
  useEffect(() => {
    console.log('[LaserEyesProvider] Inicializando...');

    // Verificar se as bibliotecas estão disponíveis
    if (typeof window !== 'undefined') {
      console.log('[LaserEyesProvider] LaserEyes SDK disponível:', {
        Provider: typeof Provider,
        MAINNET: typeof MAINNET
      });

      // Verificar imediatamente
      const hasWallets = checkWalletAvailability();

      if (!hasWallets) {
        // Se não encontrou carteiras, verificar novamente após um curto intervalo
        // Isso é útil porque algumas extensões de carteira podem levar tempo para injetar seus objetos
        const checkInterval = setInterval(() => {
          const found = checkWalletAvailability();
          if (found) {
            clearInterval(checkInterval);
            setIsReady(true);
          }
        }, 500); // Verificar a cada 500ms

        // Limpar o intervalo após 10 segundos para não continuar verificando indefinidamente
        setTimeout(() => {
          clearInterval(checkInterval);
          // Se ainda não encontrou carteiras, considerar pronto mesmo assim
          setIsReady(true);
          console.log('[LaserEyesProvider] Tempo limite atingido, continuando mesmo sem detectar carteiras');
        }, 10000);

        return () => {
          clearInterval(checkInterval);
        };
      }
    }
  }, []);

  // Log quando o componente estiver pronto
  useEffect(() => {
    if (isReady) {
      console.log('[LaserEyesProvider] Pronto para uso');
    }
  }, [isReady]);

  // Configuração do LaserEyes Provider
  return (
    <Provider
      config={{
        network: MAINNET,
        debug: true, // Ativar modo de debug para ver mais informações no console
        autoConnect: false, // Desativar conexão automática para maior controle
        // Configuração de DataSources para validação de Ordinals
        dataSources: {
          maestro: {
            apiKey: undefined // Usar chave padrão para desenvolvimento
          }
        },
        // Configurar walletProviders explicitamente
        walletProviders: ['unisat', 'xverse', 'oyl', 'leather', 'magiceden', 'okx', 'phantom', 'wizz', 'orange']
      }}
    >
      {children}
    </Provider>
  );
};

export default LaserEyesProvider;
